import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import { useCompany } from "../../../contexts/CompanyContext";

interface KPIGoal {
  id: string;
  category: string;
  title: string;
  target_value: number;
  current_value: number;
  unit: string;
  deadline: string | null;
  note: string | null;
}

const CATEGORY_COLORS: Record<string, string> = {
  capital: "#00C896", reputation: "#FF9F43", tasks: "#7A9BFF", custom: "#E8375F",
};

const AUTO_KPI = [
  { key: "capital",     label: "자본금 목표",     unit: "만원", icon: "ri-money-dollar-circle-line", defaultTarget: 10000 },
  { key: "reputation",  label: "직급점수 목표",   unit: "pt",   icon: "ri-award-line",               defaultTarget: 41 },
  { key: "tasks",       label: "태스크 완료 목표", unit: "건",   icon: "ri-checkbox-line",            defaultTarget: 10 },
];

export default function KPIPanel() {
  const { user } = useAuth();
  const { companyState, workspaceId } = useCompany();

  const [goals, setGoals] = useState<KPIGoal[]>([]);
  const [taskCount, setTaskCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [newGoal, setNewGoal] = useState({ title: "", target_value: "", unit: "", deadline: "", note: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!user || !workspaceId) return;
    setLoading(true);
    const [{ data: goalsData }, { data: tasks }] = await Promise.all([
      supabase.from("kpi_goals").select("*").eq("workspace_id", workspaceId).eq("user_id", user.id).order("created_at", { ascending: true }),
      supabase.from("task_completions").select("id", { count: "exact" }).eq("workspace_id", workspaceId).eq("user_id", user.id),
    ]);
    setGoals((goalsData ?? []) as KPIGoal[]);
    setTaskCount(tasks?.length ?? 0);
    setLoading(false);
  }, [user, workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddGoal = async () => {
    if (!user || !workspaceId || !newGoal.title.trim() || !newGoal.target_value) return;
    setSaving(true);
    await supabase.from("kpi_goals").insert({
      workspace_id: workspaceId,
      user_id: user.id,
      category: "custom",
      title: newGoal.title.trim(),
      target_value: parseFloat(newGoal.target_value),
      current_value: 0,
      unit: newGoal.unit.trim(),
      deadline: newGoal.deadline || null,
      note: newGoal.note.trim() || null,
    });
    setSaving(false);
    setNewGoal({ title: "", target_value: "", unit: "", deadline: "", note: "" });
    setShowAddForm(false);
    await fetchData();
  };

  const handleUpdateProgress = async (goalId: string, newValue: number) => {
    await supabase.from("kpi_goals").update({ current_value: newValue, updated_at: new Date().toISOString() }).eq("id", goalId);
    setGoals(prev => prev.map(g => g.id === goalId ? { ...g, current_value: newValue } : g));
  };

  const handleDelete = async (goalId: string) => {
    await supabase.from("kpi_goals").delete().eq("id", goalId);
    setGoals(prev => prev.filter(g => g.id !== goalId));
  };

  // 자동 추적 KPI 현재값
  const getAutoValue = (key: string): number => {
    if (key === "capital") return companyState?.capital ?? 0;
    if (key === "reputation") return companyState?.reputation ?? 0;
    if (key === "tasks") return taskCount;
    return 0;
  };

  if (loading) return (
    <div className="bg-[#111112] rounded-xl border border-white/[0.05] p-4">
      <div className="h-4 w-32 bg-white/[0.04] rounded animate-pulse mb-3" />
      <div className="h-20 bg-white/[0.02] rounded animate-pulse" />
    </div>
  );

  return (
    <div className="bg-[#111112] rounded-xl border border-white/[0.05] overflow-hidden">
      {/* 헤더 */}
      <button type="button" onClick={() => setCollapsed(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#E8375F]/10">
            <i className="ri-target-line text-[#E8375F] text-base"></i>
          </div>
          <div className="text-left">
            <div className="text-[#F2F2F2] text-sm font-semibold">나의 KPI 목표</div>
            <div className="text-[#444444] text-[10px]">자동 추적 3개 + 사용자 정의 {goals.length}개</div>
          </div>
        </div>
        <i className={`ri-arrow-${collapsed ? "down" : "up"}-s-line text-[#555555] text-sm`}></i>
      </button>

      {!collapsed && (
        <div className="px-4 py-4 space-y-5">
          {/* 자동 추적 KPI */}
          <div>
            <div className="text-[#3D3D3D] text-[9px] uppercase tracking-widest mb-3 flex items-center gap-1.5">
              <i className="ri-refresh-line text-xs"></i>자동 추적 목표
            </div>
            <div className="grid grid-cols-3 gap-3">
              {AUTO_KPI.map(kpi => {
                const current = getAutoValue(kpi.key);
                const pct = Math.min(100, (current / kpi.defaultTarget) * 100);
                const color = CATEGORY_COLORS[kpi.key];
                const done = pct >= 100;
                return (
                  <div key={kpi.key} className="bg-white/[0.02] rounded-xl border border-white/[0.04] p-3">
                    <div className="flex items-center justify-between mb-2">
                      <i className={`${kpi.icon} text-sm`} style={{ color }}></i>
                      {done && <i className="ri-checkbox-circle-fill text-emerald-400 text-xs"></i>}
                    </div>
                    <div className="text-[10px] text-[#666666] mb-1">{kpi.label}</div>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-sm font-bold tabular-nums" style={{ color }}>{current.toLocaleString()}</span>
                      <span className="text-[#333333] text-[9px]">/ {kpi.defaultTarget.toLocaleString()}{kpi.unit}</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: done ? "#00C896" : color }} />
                    </div>
                    <div className="text-[#333333] text-[9px] mt-1 text-right">{Math.round(pct)}%</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 사용자 정의 목표 */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[#3D3D3D] text-[9px] uppercase tracking-widest flex items-center gap-1.5">
                <i className="ri-edit-line text-xs"></i>사용자 정의 목표
              </div>
              {goals.length < 5 && !showAddForm && (
                <button type="button" onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-1 text-[10px] text-[#E8375F] hover:text-[#FF5577] cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-add-line text-xs"></i>추가
                </button>
              )}
            </div>

            {goals.length === 0 && !showAddForm && (
              <div className="text-center py-4">
                <p className="text-[#333333] text-xs">아직 목표가 없어요</p>
                <p className="text-[#2A2A2A] text-[10px] mt-0.5">개인 목표를 최대 5개까지 추가하세요</p>
              </div>
            )}

            <div className="space-y-2.5">
              {goals.map(goal => {
                const pct = Math.min(100, goal.target_value > 0 ? (goal.current_value / goal.target_value) * 100 : 0);
                const done = pct >= 100;
                return (
                  <div key={goal.id} className="bg-white/[0.01] rounded-xl border border-white/[0.05] px-3 py-2.5">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {done && <i className="ri-checkbox-circle-fill text-emerald-400 text-xs shrink-0"></i>}
                        <span className="text-[#CCCCCC] text-xs font-medium truncate">{goal.title}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[#555555] text-[10px] tabular-nums">
                          {goal.current_value.toLocaleString()}/{goal.target_value.toLocaleString()}{goal.unit}
                        </span>
                        <button type="button" onClick={() => handleDelete(goal.id)}
                          className="w-4 h-4 flex items-center justify-center text-[#3D3D3D] hover:text-[#E8375F] cursor-pointer transition-colors">
                          <i className="ri-delete-bin-line text-[10px]"></i>
                        </button>
                      </div>
                    </div>
                    <div className="w-full h-1.5 bg-white/[0.05] rounded-full overflow-hidden mb-2">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: done ? "#00C896" : CATEGORY_COLORS.custom }} />
                    </div>
                    {/* 진행도 수동 업데이트 */}
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        defaultValue={goal.current_value}
                        onBlur={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v !== goal.current_value) handleUpdateProgress(goal.id, v); }}
                        className="w-20 bg-[#0C0C0E] text-[#888888] text-[10px] px-2 py-1 rounded border border-white/[0.06] focus:outline-none focus:border-[#E8375F]/30 tabular-nums"
                        placeholder="현재값"
                      />
                      <span className="text-[#3D3D3D] text-[9px]">{goal.unit} 입력 후 Enter/클릭 이탈</span>
                      {goal.deadline && (
                        <span className="text-[#3D3D3D] text-[9px] ml-auto">~{goal.deadline}</span>
                      )}
                    </div>
                    {goal.note && <p className="text-[#333333] text-[9px] mt-1 leading-relaxed">{goal.note}</p>}
                  </div>
                );
              })}
            </div>

            {/* 추가 폼 */}
            {showAddForm && (
              <div className="bg-white/[0.02] rounded-xl border border-[#E8375F]/20 px-3 py-3 space-y-2.5 mt-2.5">
                <div className="text-[#E8375F] text-[10px] font-semibold mb-2">새 목표 추가</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[#444444] text-[10px] block mb-1">목표 이름</label>
                    <input type="text" value={newGoal.title} onChange={e => setNewGoal(f => ({ ...f, title: e.target.value }))}
                      placeholder="예: 협업 메일 10건 발송" maxLength={50}
                      className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs px-2.5 py-1.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#E8375F]/30 placeholder-[#2A2A2A]" />
                  </div>
                  <div className="flex gap-1.5">
                    <div className="flex-1">
                      <label className="text-[#444444] text-[10px] block mb-1">목표값</label>
                      <input type="number" value={newGoal.target_value} onChange={e => setNewGoal(f => ({ ...f, target_value: e.target.value }))}
                        placeholder="10"
                        className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs px-2.5 py-1.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#E8375F]/30 placeholder-[#2A2A2A]" />
                    </div>
                    <div style={{ width: 60 }}>
                      <label className="text-[#444444] text-[10px] block mb-1">단위</label>
                      <input type="text" value={newGoal.unit} onChange={e => setNewGoal(f => ({ ...f, unit: e.target.value }))}
                        placeholder="건"
                        className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs px-2 py-1.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#E8375F]/30 placeholder-[#2A2A2A]" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[#444444] text-[10px] block mb-1">마감일 (선택)</label>
                    <input type="date" value={newGoal.deadline} onChange={e => setNewGoal(f => ({ ...f, deadline: e.target.value }))}
                      className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs px-2.5 py-1.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#E8375F]/30" />
                  </div>
                  <div>
                    <label className="text-[#444444] text-[10px] block mb-1">메모 (선택)</label>
                    <input type="text" value={newGoal.note} onChange={e => setNewGoal(f => ({ ...f, note: e.target.value }))}
                      placeholder="간단한 메모"
                      className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs px-2.5 py-1.5 rounded-lg border border-white/[0.08] focus:outline-none focus:border-[#E8375F]/30 placeholder-[#2A2A2A]" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setShowAddForm(false)}
                    className="flex-1 py-2 rounded-lg border border-white/[0.08] text-[#555555] text-xs hover:text-[#888888] cursor-pointer transition-colors whitespace-nowrap">취소</button>
                  <button type="button" onClick={handleAddGoal} disabled={!newGoal.title.trim() || !newGoal.target_value || saving}
                    className="flex-1 py-2 rounded-lg bg-[#E8375F] text-white text-xs font-semibold hover:bg-[#FF5577] cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50">
                    {saving ? "추가 중..." : "목표 추가"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
